import React, { useState, useEffect } from 'react';
import { LuX, LuHistory, LuListTodo, LuFileText, LuSend, LuCheck, LuCircle } from 'react-icons/lu';
import { format } from 'date-fns';
import { opportunitiesService } from '../../services/opportunitiesService';
import { settingsService, SETTINGS_KEYS } from '../../services/settingsService';
import { OPPORTUNITY_STAGES } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../hooks/useAuth';

const TABS = {
    OVERVIEW: 'overview',
    ACTIVITY: 'activity',
    PROCESS: 'process'
};

const OpportunityDetailsModal = ({ isOpen, onClose, opportunity, onUpdate }) => {
    const { user, userProfile } = useAuth();
    const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);
    const [history, setHistory] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [submittingNote, setSubmittingNote] = useState(false);

    // Checklist State
    const [checklistsConfig, setChecklistsConfig] = useState({});
    const [checklistData, setChecklistData] = useState({});
    const [loadingChecklist, setLoadingChecklist] = useState(true);

    useEffect(() => {
        if (isOpen && opportunity) {
            fetchHistory();
            fetchChecklistSettings();
            setChecklistData(opportunity.checklist_data || {});
        }
    }, [isOpen, opportunity]);

    const fetchChecklistSettings = async () => {
        setLoadingChecklist(true);
        const result = await settingsService.getSettings(SETTINGS_KEYS.OPPORTUNITY_CHECKLISTS);
        if (result.success && result.data) {
            setChecklistsConfig(result.data);
        }
        setLoadingChecklist(false);
    };

    const fetchHistory = async () => {
        if (!opportunity) return;
        setLoadingHistory(true);
        try {
            const result = await opportunitiesService.getHistory(opportunity.id);
            if (result.success) {
                setHistory(result.data);
            }
        } catch (error) {
            console.error('Failed to fetch history', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;

        setSubmittingNote(true);
        try {
            const result = await opportunitiesService.addHistoryEntry({
                opportunity_id: opportunity.id,
                old_stage: opportunity.stage,
                new_stage: opportunity.stage, // No stage change, just note
                notes: newNote.trim()
            });

            if (result.success) {
                setNewNote('');
                fetchHistory(); // Refresh list
            }
        } catch (error) {
            console.error('Failed to add note', error);
        } finally {
            setSubmittingNote(false);
        }
    };

    const handleToggleChecklistItem = async (itemText) => {
        const stage = opportunity.stage;
        const currentStageData = checklistData[stage] || {};
        const isCompleted = currentStageData[itemText]?.completed;

        let newItemData;
        if (isCompleted) {
            // Uncheck
            newItemData = { ...currentStageData, [itemText]: { completed: false } };
        } else {
            // Check
            newItemData = {
                ...currentStageData,
                [itemText]: {
                    completed: true,
                    completedBy: user.id,
                    completedByName: userProfile?.full_name || user.user_metadata?.full_name || 'User',
                    completedAt: new Date().toISOString()
                }
            };
        }

        const newChecklistData = { ...checklistData, [stage]: newItemData };
        setChecklistData(newChecklistData);

        // Optimistic update locally, then save to DB
        // If onUpdate is provided, use it to sync up
        // Also call service to persist
        try {
            await opportunitiesService.updateOpportunity(opportunity.id, {
                checklist_data: newChecklistData
            });
            if (onUpdate) onUpdate(); // Refresh parent view if needed
        } catch (error) {
            console.error('Failed to save checklist', error);
            // Revert on error? For simplicity, keeping optimistic state unless reload
        }
    };

    if (!isOpen || !opportunity) return null;

    const renderOverview = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
                <div>
                    <h4 className="text-sm font-medium text-gray-500">Amount</h4>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: opportunity.currency }).format(opportunity.amount)}
                    </p>
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-500">Expected Close</h4>
                    <p className="mt-1 text-lg text-gray-900">
                        {opportunity.close_date ? format(new Date(opportunity.close_date), 'MMM d, yyyy') : 'Not set'}
                    </p>
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-500">Stage</h4>
                    <span className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                        {opportunity.stage}
                    </span>
                </div>
                <div>
                    <h4 className="text-sm font-medium text-gray-500">Probability</h4>
                    <p className="mt-1 text-lg text-gray-900">{opportunity.probability || 0}%</p>
                </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Account</h4>
                <p className="text-base text-gray-900">{opportunity.accounts?.name}</p>
            </div>

            <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Description / Notes</h4>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{opportunity.description || 'No description provided.'}</p>
            </div>
        </div>
    );

    const renderActivity = () => (
        <div className="space-y-6">
            {/* Add Note Section */}
            <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Add Note / Update</h4>
                <form onSubmit={handleAddNote}>
                    <textarea
                        className="w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                        rows="3"
                        placeholder="Type your note here..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                    ></textarea>
                    <div className="mt-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={submittingNote || !newNote.trim()}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            <SafeIcon icon={LuSend} className="h-4 w-4 mr-2" />
                            Post Note
                        </button>
                    </div>
                </form>
            </div>

            {/* Timeline */}
            <div className="flow-root">
                <ul className="-mb-8">
                    {history.map((item, itemIdx) => (
                        <li key={item.id}>
                            <div className="relative pb-8">
                                {itemIdx !== history.length - 1 ? (
                                    <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                                ) : null}
                                <div className="relative flex space-x-3">
                                    <div>
                                        <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${item.old_stage !== item.new_stage ? 'bg-blue-500' : 'bg-gray-400'
                                            }`}>
                                            {item.old_stage !== item.new_stage ? (
                                                <SafeIcon icon={LuHistory} className="h-5 w-5 text-white" />
                                            ) : (
                                                <SafeIcon icon={LuFileText} className="h-5 w-5 text-white" />
                                            )}
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1 pt-1.5 justify-between space-y-1">
                                        <div>
                                            <p className="text-sm text-gray-500">
                                                <span className="font-medium text-gray-900">{item.users?.full_name || 'System'}</span>
                                                {' '}
                                                {item.old_stage !== item.new_stage ? (
                                                    <span>changed stage from <span className="font-medium">{item.old_stage}</span> to <span className="font-medium">{item.new_stage}</span></span>
                                                ) : (
                                                    <span>added a note</span>
                                                )}
                                            </p>
                                        </div>
                                        {item.notes && (
                                            <div className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                                                <p>{item.notes}</p>
                                            </div>
                                        )}
                                        <div className="text-right text-xs text-gray-400">
                                            {format(new Date(item.changed_at), 'MMM d, h:mm a')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    ))}
                    {history.length === 0 && (
                        <li className="text-center text-gray-500 py-4">No history yet.</li>
                    )}
                </ul>
            </div>
        </div>
    );

    const renderProcess = () => {
        const stage = opportunity.stage;
        const checklist = checklistsConfig[stage] || [];
        const stageData = checklistData[stage] || {};

        const totalItems = checklist.length;
        const completedItems = checklist.filter(item => stageData[item]?.completed).length;
        const progress = totalItems === 0 ? 0 : Math.round((completedItems / totalItems) * 100);

        return (
            <div className="space-y-6">
                {/* Header & Progress Bar */}
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-end mb-2">
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">Stage Progress</h3>
                            <p className="text-sm text-gray-500">Complete tasks to advance the opportunity.</p>
                        </div>
                        <span className="text-2xl font-bold text-blue-600">{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                </div>

                {/* Checklist */}
                <div className="space-y-3">
                    {checklist.map((item, idx) => {
                        const itemData = stageData[item];
                        const isChecked = itemData?.completed;

                        return (
                            <div
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleChecklistItem(item);
                                }}
                                className={`group p-4 rounded-lg border cursor-pointer transition-all ${isChecked
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
                                    }`}
                            >
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 mt-0.5" onClick={(e) => e.stopPropagation()}>
                                        {isChecked ? (
                                            <SafeIcon icon={LuCheck} className="h-6 w-6 text-blue-600" />
                                        ) : (
                                            <SafeIcon icon={LuCircle} className="h-6 w-6 text-gray-300 group-hover:text-blue-400" />
                                        )}
                                    </div>
                                    <div className="ml-3 flex-grow">
                                        <p className={`text-base font-medium ${isChecked ? 'text-blue-900' : 'text-gray-900'}`}>
                                            {item}
                                        </p>
                                        {isChecked && itemData && (
                                            <p className="mt-1 text-xs text-blue-600 flex items-center flex-wrap gap-1">
                                                <span>Checked by <strong>{itemData.completedByName}</strong></span>
                                                <span className="text-blue-400">•</span>
                                                <span>{format(new Date(itemData.completedAt), 'MMM d, h:mm a')}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {checklist.length === 0 && (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg dashed-border">
                            No checklist items configured for this stage.
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                    <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="flex justify-between items-start p-6 border-b border-gray-200">
                        <div>
                            <h3 className="text-xl leading-6 font-semibold text-gray-900">
                                {opportunity.name}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {opportunity.accounts?.name}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                            <SafeIcon icon={LuX} className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex px-6 space-x-8" aria-label="Tabs">
                            {[
                                { id: TABS.OVERVIEW, name: 'Overview', icon: LuFileText },
                                { id: TABS.PROCESS, name: 'Process & Tasks', icon: LuListTodo },
                                { id: TABS.ACTIVITY, name: 'Activity Log', icon: LuHistory },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                                >
                                    <SafeIcon icon={tab.icon} className="mr-2 h-4 w-4" />
                                    {tab.name}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="p-6 h-[500px] overflow-y-auto">
                        {activeTab === TABS.OVERVIEW && renderOverview()}
                        {activeTab === TABS.PROCESS && renderProcess()}
                        {activeTab === TABS.ACTIVITY && renderActivity()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OpportunityDetailsModal;
